import { useRef, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { captureException, captureMessage } from '@sentry/react'
import type { AxiosError } from 'axios'
import { useSearchParams } from 'react-router-dom'
import RiveComponent, { RiveAlignment, RiveFit } from '../components/RiveComponent'
import * as rive from '@rive-app/canvas'
import { goBack, openEliteLandingPage, onReady, trackEvent, updateAppStatusBar } from '../utils/appBridge'
import { fetchEliteUsersWrap } from '../repository/eliteWrapped'
import { eliteWrappedMockData } from '../data/eliteWrappedMockData'
import { eliteWrappedToTextRunValues } from '../utils/eliteWrappedTextRuns'
import { applyEliteRenewTextRun } from '../utils/applyEliteRenewTextRun'
import { logRiveTextRuns } from '../utils/logRiveTextRuns'
import RenderHTML from '../components/ui-components/RenderHTML'
import { EventNames } from '../types/misc.types'
import { getBottomPadding, getTopPadding } from '../utils/sessionStorage'
import { isIOS } from '../utils/utils'

const ELITE_RECAP_RIVE_SRC = '/rives/elite-recap.riv'
const ELITE_RECAP_ARTBOARD = 'Elite-Recap 2'
const ELITE_RECAP_STATE_MACHINE = 'State Machine 1'
const SCREEN_NAME = 'EliteWrapped'

export const EliteWrappedRivePage = () => {
  const eventsListRef = useRef<Set<string>>(new Set())
  const onReadyLock = useRef(false)
  const [searchParams] = useSearchParams()

  const { data: apiData, isSuccess, isError, error } = useQuery({
    queryKey: ['eliteUsersWrap'],
    queryFn: fetchEliteUsersWrap,
    retry: 1,
    refetchOnWindowFocus: false
  })

  const sentryReportedRef = useRef({ success: false, error: false })

  useEffect(() => {
    if (!isSuccess || !apiData || sentryReportedRef.current.success) return
    sentryReportedRef.current.success = true
    captureMessage('Elite users wrap loaded', {
      level: 'info',
      tags: { screen: SCREEN_NAME, query: 'eliteUsersWrap' },
      extra: {
        eliteRenewalStatus: apiData?.eliteRenewalStatus,
        frameCount: apiData?.frameData?.length ?? 0,
        response: apiData
      }
    })
  }, [isSuccess, apiData])

  useEffect(() => {
    if (!isError || sentryReportedRef.current.error) return
    sentryReportedRef.current.error = true
    const axiosError = error as AxiosError | undefined
    captureException(error instanceof Error ? error : new Error('Elite users wrap request failed'), {
      tags: { screen: SCREEN_NAME, query: 'eliteUsersWrap' },
      extra: {
        httpStatus: axiosError?.response?.status,
        responseBody: axiosError?.response?.data,
        message: axiosError?.message ?? (error ? String(error) : undefined)
      }
    })
  }, [isError, error])

  const wrapData = apiData ?? (isError ? eliteWrappedMockData : undefined)
  const riveInstanceRef = useRef<rive.Rive | null>(null)
  const isWrapDataReady = Boolean(isSuccess || isError)

  const textRunValues = useMemo(() => eliteWrappedToTextRunValues(wrapData), [wrapData])

  useEffect(() => {
    const source = searchParams.get('source') || undefined
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ScreenName: SCREEN_NAME,
        screenType: SCREEN_NAME,
        ...(source && { source })
      } as Record<string, string>
    })
  }, [searchParams])

  const handleRiveEvent = (event: rive.Event) => {
    if (event.type !== rive.EventType.RiveEvent || !event.data) return

    const riveEventData = event.data as rive.RiveEventPayload
    if (!riveEventData || !('name' in riveEventData)) return

    const eventName = riveEventData.name
    if (!eventsListRef.current.has(eventName)) {
      eventsListRef.current.add(eventName)
    }

    if (eventName === 'Next') {
      trackEvent({
        eventName: EventNames.ButtonTap,
        payload: { eventId: 'eliteRecapNext', ScreenName: SCREEN_NAME }
      })
      return
    }

    if (eventName === 'Back') {
      trackEvent({
        eventName: EventNames.ButtonTap,
        payload: { eventId: 'eliteRecapBack', ScreenName: SCREEN_NAME }
      })
      return
    }

    if (eventName === 'Finish') {
      trackEvent({
        eventName: EventNames.ButtonTap,
        payload: { eventId: 'finishEliteRecap', ScreenName: SCREEN_NAME }
      })
      goBack()
      return
    }

    if (eventName === 'Elite_Renew' || eventName === 'Get Elite') {
      trackEvent({
        eventName: EventNames.ButtonTap,
        payload: { eventId: 'eliteRenew', ScreenName: SCREEN_NAME }
      })
      openEliteLandingPage()
    }
  }

  const handlePageReady = () => {
    if (!onReadyLock.current) {
      onReadyLock.current = true
      onReady()
    }
  }

  useEffect(() => {
    const readyTimer = setTimeout(() => handlePageReady(), 1000)
    updateAppStatusBar('000000', 'light')
    return () => clearTimeout(readyTimer)
  }, [])

  useEffect(() => {
    if (isSuccess || isError) {
      const readyTimer = setTimeout(() => handlePageReady(), 150)
      return () => clearTimeout(readyTimer)
    }
  }, [isSuccess, isError])

  const handleRiveLoad = (riveInstance: rive.Rive) => {
    riveInstanceRef.current = riveInstance
    applyEliteRenewTextRun(riveInstance, wrapData?.eliteRenewalStatus)
    logRiveTextRuns(riveInstance, 'elite-recap (after apply)')
  }

  useEffect(() => {
    if (!riveInstanceRef.current || !wrapData?.eliteRenewalStatus) return
    applyEliteRenewTextRun(riveInstanceRef.current, wrapData.eliteRenewalStatus)
  }, [wrapData?.eliteRenewalStatus])

  const handleClose = () => {
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: { eventId: 'close', ScreenName: SCREEN_NAME }
    })
    goBack()
  }

  return (
    <div className='relative flex flex-col items-center justify-center h-screen w-full bottom-0 bg-white'>
      <div
        onClick={handleClose}
        style={{ top: `${getTopPadding() + 8}px` }}
        className='absolute right-4 z-50 w-9 h-9 flex items-center justify-center rounded-full cursor-pointer'
        aria-label='Close'
      >
        <RenderHTML innerHTMLText={WRAPPED_CLOSE_BUTTON_SVG} />
      </div>

      <div style={{ paddingBottom: `${isIOS() ? 4 : getBottomPadding() + 4}px` }} className='w-full h-full'>
        {isWrapDataReady && wrapData?.frameData?.length ? (
          <RiveComponent
            src={ELITE_RECAP_RIVE_SRC}
            autoplay
            fit={RiveFit.COVER}
            alignment={RiveAlignment.BOTTOM_CENTER}
            stateMachines={ELITE_RECAP_STATE_MACHINE}
            artboard={ELITE_RECAP_ARTBOARD}
            onRiveEventReceived={handleRiveEvent}
            onLoad={handleRiveLoad}
            textRunValues={textRunValues}
          />
        ) : null}
      </div>
    </div>
  )
}

const WRAPPED_CLOSE_BUTTON_SVG = `
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="14" cy="14" r="14" fill="white"/>
    <path d="M17.9579 8.62505C18.3484 8.23458 18.9815 8.23468 19.372 8.62505C19.7625 9.01557 19.7625 9.6486 19.372 10.0391L15.412 13.9981L19.372 17.9581C19.7624 18.3486 19.7625 18.9816 19.372 19.3721C18.9815 19.7626 18.3485 19.7626 17.9579 19.3721L13.998 15.4122L10.039 19.3721C9.64846 19.7625 9.01541 19.7626 8.62492 19.3721C8.2346 18.9816 8.23458 18.3485 8.62492 17.9581L12.5839 13.9981L8.62492 10.0391C8.2344 9.64858 8.2344 9.01557 8.62492 8.62505C9.01545 8.23452 9.64846 8.23452 10.039 8.62505L13.998 12.584L17.9579 8.62505Z" fill="#232323"/>
  </svg>
`
